import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowUpRight, BadgeCheck, ChevronLeft, ChevronRight, Crown, Gem, Globe, Link2, Mail, Trophy, Users } from 'lucide-react';
import api from '../../services/api';
import SafeImage from '../../components/SafeImage';
import { DiscordGlyph, FacebookGlyph, InstagramGlyph, TelegramGlyph, TikTokGlyph, WhatsAppGlyph, YoutubeGlyph } from './components/Glyphs';
import './brand-profile.css';

const SOCIALS = [
  { key: 'instagram', label: 'Instagram', icon: InstagramGlyph, asset: 'instagram', cta: 'Follow', subLabel: h => handleFromUrl(h, 'instagram.com') },
  { key: 'youtube', label: 'YouTube', icon: YoutubeGlyph, cta: 'Subscribe' },
  { key: 'tiktok', label: 'TikTok', icon: TikTokGlyph, cta: 'Follow', subLabel: h => handleFromUrl(h, 'tiktok.com') },
  { key: 'facebook', label: 'Facebook', icon: FacebookGlyph, asset: 'facebook', cta: 'Like' },
  { key: 'whatsapp', label: 'WhatsApp', icon: WhatsAppGlyph, asset: 'whatsapp', cta: 'Chat', subLabel: h => String(h).replace(/^https?:\/\//, '') },
  { key: 'discord', label: 'Discord', icon: DiscordGlyph, cta: 'Join', subLabel: () => 'Join our community' },
  { key: 'telegram', label: 'Telegram', icon: TelegramGlyph, cta: 'Join', subLabel: h => handleFromUrl(h, 't.me') },
  { key: 'website', label: 'Website', icon: Globe, cta: 'Visit' },
  { key: 'support_email', label: 'Email', icon: Mail, cta: 'Email', subLabel: h => h },
];
const STATS = [
  { key: 'followers', label: 'Followers', icon: Users },
  { key: 'games', label: 'Games', icon: Trophy },
  { key: 'rewards_claimed', label: 'Rewards Claimed', icon: Gem },
];

function handleFromUrl(url, domain) {
  try {
    const match = String(url).match(new RegExp(`${domain.replace(/\./g, '\\.')}/@?([A-Za-z0-9_\\.\\-]+)`));
    return match ? `@${match[1]}` : '';
  } catch { return ''; }
}

export function socialHref(key, value) {
  const raw = String(value || '').trim();
  if (!raw || /^(javascript|data|vbscript):/i.test(raw)) return '';
  const extracted = raw.match(/https?:\/\/[^\s]+/i)?.[0]?.replace(/[),.;]+$/, '');
  if (extracted) return extracted;
  if (key === 'support_email') return raw.includes('@') ? `mailto:${raw.replace(/^mailto:/i, '')}` : '';
  if (key === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (key === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    const digits = raw.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  return `https://${raw}`;
}

export function socialValue(branding, key) {
  const aliases = {
    facebook: ['facebook', 'facebook_url'], whatsapp: ['whatsapp', 'whatsapp_url'],
    discord: ['discord', 'discord_url'], telegram: ['telegram', 'telegram_url'], support_email: ['support_email', 'email'],
  };
  for (const candidate of aliases[key] || [key]) {
    const value = branding?.socials?.[candidate] || branding?.support?.[candidate];
    if (value) return value;
  }
  return '';
}

function LoadingProfile() {
  return <div className="bp-loading" role="status" aria-label="Loading brand profile">
    <span className="bp-sr-only">Loading brand profile…</span>
    <div className="bp-skeleton-identity" aria-hidden="true"><i /><span /><span /></div>
    <div className="bp-skeleton-links" aria-hidden="true"><i /><i /><i /><i /></div>
  </div>;
}

export default function BrandProfilePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const [refresh, setRefresh] = useState(0);
  const [snapshot, setSnapshot] = useState({ status: 'loading', scope: tenantSlug });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setSnapshot({ status: 'loading', scope: tenantSlug });
    api.get('/api/branding', { signal: controller.signal, timeout: 15000 })
      .then(({ data }) => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid brand profile');
        if (active) setSnapshot({ status: 'ready', data, scope: tenantSlug });
      })
      .catch(() => { if (active) setSnapshot({ status: 'error', scope: tenantSlug }); });
    return () => { active = false; controller.abort(); };
  }, [tenantSlug, refresh]);

  const ready = snapshot.scope === tenantSlug && snapshot.status === 'ready';
  const error = snapshot.scope === tenantSlug && snapshot.status === 'error';
  const b = ready ? snapshot.data : {};
  const displayName = b.brand_name || 'Brand profile';
  const socialLinks = SOCIALS.map(def => ({ def, value: socialValue(b, def.key) }))
    .filter(({ def, value }) => b.social_visibility?.[def.key] !== false && socialHref(def.key, value));
  const stats = STATS.filter(({ key }) => b.stats?.[key] !== undefined && b.stats?.[key] !== null && b.stats?.[key] !== '');
  const supportHref = socialHref('support_email', socialValue(b, 'support_email'));

  return <div className="player-app brand-profile-page" data-testid="brand-profile">
    <header className="bp-header"><div className="bp-header-inner">
      <button type="button" onClick={() => navigate(homePath)} className="bp-back" aria-label="Back" data-testid="brand-profile-back"><ChevronLeft size={20} aria-hidden="true" /></button>
      <p className="bp-header-title">{ready ? displayName : 'Brand profile'}</p><span className="bp-header-spacer" aria-hidden="true" />
    </div></header>
    <main className="bp-content">
      {!ready && !error ? <LoadingProfile /> : error ? <section className="bp-error" role="alert">
        <Crown size={32} aria-hidden="true" /><h1>Brand profile couldn’t be loaded.</h1>
        <p>Check your connection and try again.</p>
        <button type="button" className="bp-retry" onClick={() => setRefresh(n => n + 1)}>Try again</button>
      </section> : <>
        <section className="bp-identity" aria-label="Brand details">
          <div className="bp-logo-frame">
            <SafeImage src={b.logo_url} alt={displayName} width={128} height={128} className="bp-logo" data-testid="brand-profile-logo" loading="eager" fallback={<Crown size={58} className="bp-logo-fallback" aria-hidden="true" />} />
            {b.verified && <span className="bp-logo-verified" aria-hidden="true"><BadgeCheck size={27} /></span>}
          </div>
          <h1>{displayName}</h1>
          {b.verified && <p className="bp-verified" data-testid="brand-profile-verified"><BadgeCheck size={16} aria-hidden="true" />Official Brand</p>}
          {b.description && <p className="bp-description" data-testid="brand-profile-description">{b.description}</p>}
          {stats.length > 0 && <dl className="bp-stats" style={{ '--bp-stat-count': stats.length }} data-testid="brand-profile-stats">
            {stats.map(({ key, label, icon: Icon }) => <div className="bp-stat" key={key}><Icon size={18} aria-hidden="true" /><dt>{label}</dt><dd>{b.stats[key]}</dd></div>)}
          </dl>}
        </section>
        <section className="bp-connect" aria-labelledby="bp-connect-title">
          <h2 id="bp-connect-title"><Link2 size={19} aria-hidden="true" />Connect With Us</h2>
          <div className="bp-social-list">
            {socialLinks.length ? socialLinks.map(({ def, value }) => <SocialRow key={def.key} def={def} value={value} brand={displayName} />) : <p className="bp-empty">No social links configured yet.</p>}
          </div>
          {b.support?.enabled !== false && supportHref && <a href={supportHref} className="bp-support" data-testid="brand-profile-support">
            <span className="bp-support-icon" aria-hidden="true"><Mail size={23} /></span>
            <span className="bp-support-copy"><strong>Need Help?</strong><span>Our support team is here 24/7</span></span>
            <span className="bp-support-action">Contact Support<ArrowUpRight size={16} aria-hidden="true" /></span>
          </a>}
        </section>
      </>}
    </main>
  </div>;
}

function SocialRow({ def, value, brand }) {
  const Icon = def.icon;
  const fallback = <span className={`bp-social-fallback bp-social-fallback-${def.key}`}><Icon size={25} /></span>;
  return <a href={socialHref(def.key, value)} target="_blank" rel="noopener noreferrer" className="bp-social-row" data-testid={`brand-profile-social-${def.key}`}>
    <span className="bp-social-mark" aria-hidden="true">
      {def.asset ? <SafeImage src={`/assets/brand-profile/${def.asset}.png`} alt="" width={60} height={60} className="bp-generated-icon" loading="lazy" retry={0} fallback={fallback} /> : fallback}
    </span>
    <span className="bp-social-copy"><strong>{def.label}</strong><span>{def.subLabel?.(value) || brand}</span></span>
    <span className="bp-social-action">{def.cta}<ChevronRight size={17} aria-hidden="true" /></span>
  </a>;
}
